import { useCallback, useEffect, useRef, useState } from 'react';
import { useOnline } from '@/hooks';
import { logger } from '@/lib/logger';
import { outbox, transcriptOutbox, xrayOutbox } from '@/lib/outbox';
import { entriesApi, transcriptsApi, xraysApi } from '../chart/api';
import { syncOutbox, syncTranscripts, syncXrays } from './syncOutbox';

/**
 * Dictations first (entries refer to them), then entries, then X-rays. A
 * network problem stops the run; the next one picks up where it stopped.
 */
async function sendAll(userId: string) {
  const steps = [
    ['transcripts', () => syncTranscripts(transcriptOutbox, transcriptsApi.saveItem, userId)],
    ['entries', () => syncOutbox(outbox, entriesApi.create, userId)],
    ['x-rays', () => syncXrays(xrayOutbox, xraysApi.upload, userId)],
  ] as const;
  for (const [what, run] of steps) {
    const result = await run();
    if (result.rejected) logger.warn(`outbox ${what} refused`, result);
    if (result.stopped) return;
  }
}

/** Sends the signed-in user's queued offline dictations, entries and X-rays whenever the device is online. */
export function useOutboxSync(userId: string | null) {
  const online = useOnline();
  const [count, setCount] = useState(0);
  const [failed, setFailed] = useState(0);
  const running = useRef(false);

  const refresh = useCallback(async () => {
    const mine = [
      ...(await outbox.all()),
      ...(await transcriptOutbox.all()),
      ...(await xrayOutbox.all()),
    ].filter((i) => i.authorId === userId);
    setCount(mine.length);
    setFailed(mine.filter((i) => i.failed).length);
  }, [userId]);

  const sync = useCallback(async () => {
    if (!userId || running.current || !navigator.onLine) return;
    running.current = true;
    try {
      await sendAll(userId);
    } catch (err) {
      logger.warn('outbox sync failed', err);
    } finally {
      running.current = false;
      await refresh();
    }
  }, [userId, refresh]);

  useEffect(() => {
    void refresh();
    return outbox.subscribe(() => void refresh());
  }, [refresh]);

  useEffect(() => {
    if (!(userId && online)) return;
    void sync();
    const t = setInterval(() => void sync(), 30000);
    return () => clearInterval(t);
  }, [userId, online, sync]);

  return { pending: count, failed, online, sync };
}
